import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import {
  Copy,
  Edit,
  ExternalLink,
  Trash2,
  MapPin,
  Image as ImageIcon,
  RefreshCw,
} from 'lucide-react';

interface HeroData {
  name: string;
  image_url: string;
  click_url: string;
}

interface Municipality {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  heroes_data: HeroData[];
  municipality_web_url: string;
  newsletter_url: string;
}

interface MunicipalityCardProps {
  municipality: Municipality;
  onEdit: (municipality: Municipality) => void;
  onDelete?: (id: string, name: string) => void;
  onManageScreensaver?: (municipality: Municipality) => void;
  onReloadDevices?: (municipality: Municipality) => void;
}

export const MunicipalityCard = ({
  municipality,
  onEdit,
  onDelete,
  onManageScreensaver,
  onReloadDevices,
}: MunicipalityCardProps) => {
  const { toast } = useToast();
  const kioskPath = `/?id=${encodeURIComponent(municipality.id)}`;
  const kioskUrl = new URL(kioskPath, window.location.origin).toString();

  const copyToClipboard = async (value: string, description: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast({
        title: 'Copiado',
        description,
      });
    } catch {
      toast({
        title: 'No se pudo copiar',
        description: 'Selecciona el texto y cópialo manualmente.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Card className="group shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border-2 border-border bg-card hover:border-primary/40 overflow-hidden">
      <CardContent className="p-0">
        {/* Image preview section */}
        <div className="relative h-32 md:h-40 bg-muted overflow-hidden">
          {municipality.heroes_data && municipality.heroes_data.length > 0 && municipality.heroes_data[0].image_url ? (
            <div className="w-full h-full flex gap-1">
              {municipality.heroes_data.slice(0, 3).map((hero, idx) => (
                <div key={idx} className="flex-1 relative">
                  {hero.image_url ? (
                    <img
                      src={hero.image_url}
                      alt={hero.name}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-muted/50">
                      <ImageIcon className="h-6 w-6 md:h-8 md:w-8 text-muted-foreground/30" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-muted">
              <ImageIcon className="h-12 w-12 md:h-16 md:w-16 text-muted-foreground/30" />
            </div>
          )}
          
          {/* Badges overlay */}
          <div className="absolute top-2 right-2 md:top-3 md:right-3 flex gap-2">
            {municipality.heroes_data && municipality.heroes_data.length > 0 && (
              <Badge variant="secondary" className="bg-primary/90 text-primary-foreground backdrop-blur-sm text-xs">
                {municipality.heroes_data.length} {municipality.heroes_data.length === 1 ? 'Hero' : 'Heroes'}
              </Badge>
            )}
          </div>
        </div>

        {/* Content section */}
        <div className="p-4 md:p-6">
          <div className="flex justify-between items-start mb-3 md:mb-4">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg md:text-xl font-bold text-foreground mb-1 md:mb-2 group-hover:text-primary transition-colors truncate">
                {municipality.name}
              </h3>
              <div className="flex items-center gap-1.5 md:gap-2 text-xs md:text-sm text-muted-foreground">
                <MapPin className="h-3 w-3 md:h-4 md:w-4 flex-shrink-0" />
                <span className="truncate">{parseFloat(municipality.latitude as any).toFixed(4)}, {parseFloat(municipality.longitude as any).toFixed(4)}</span>
              </div>
            </div>
            
            {/* Action buttons */}
            <div className="flex gap-2 ml-2 md:ml-4 flex-shrink-0">
              <Button
                variant="outline"
                size="icon"
                onClick={() => onEdit(municipality)}
                className="h-8 w-8 md:h-10 md:w-10 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors"
              >
                <Edit className="h-3.5 w-3.5 md:h-4 md:w-4" />
              </Button>
              
              {onDelete && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => onDelete(municipality.id, municipality.name)}
                  className="h-8 w-8 md:h-10 md:w-10 hover:bg-destructive hover:text-destructive-foreground hover:border-destructive transition-colors"
                >
                  <Trash2 className="h-3.5 w-3.5 md:h-4 md:w-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Identificador y URL para configurar el tótem */}
          <div className="mb-3 md:mb-4 rounded-md border bg-muted/40 p-2.5 text-xs">
            <div className="mb-1 font-medium text-foreground">ID del municipio</div>
            <code
              className="block break-all font-mono text-muted-foreground"
              title={municipality.id}
            >
              {municipality.id}
            </code>
            <code
              className="mt-1.5 block truncate font-mono text-muted-foreground"
              title={kioskUrl}
            >
              {kioskPath}
            </code>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() =>
                  copyToClipboard(
                    municipality.id,
                    `ID de ${municipality.name} copiado.`,
                  )
                }
              >
                <Copy className="mr-1.5 h-3.5 w-3.5" />
                Copiar ID
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() =>
                  copyToClipboard(
                    kioskUrl,
                    `URL del tótem de ${municipality.name} copiada.`,
                  )
                }
              >
                <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                Copiar URL
              </Button>
            </div>
          </div>

          {/* URLs info */}
          <div className="space-y-1.5 md:space-y-2 text-xs md:text-sm">
            {municipality.heroes_data && municipality.heroes_data.length > 0 && (
              <div className="space-y-1 mb-2">
                <span className="font-medium text-foreground">Heroes:</span>
                {municipality.heroes_data.map((hero, idx) => (
                  <div key={idx} className="flex items-start gap-1.5 md:gap-2 ml-2 text-xs overflow-hidden">
                    <span className="font-medium text-foreground flex-shrink-0">{hero.name}:</span>
                    <span className="text-muted-foreground truncate max-w-[150px] md:max-w-none" title={hero.click_url}>
                      {hero.click_url}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-start gap-1.5 md:gap-2 overflow-hidden">
              <span className="font-medium text-foreground min-w-[40px] md:min-w-[80px] flex-shrink-0">Web:</span>
              <span className="text-muted-foreground truncate max-w-[150px] md:max-w-none" title={municipality.municipality_web_url}>
                {municipality.municipality_web_url}
              </span>
            </div>
          </div>

          {/* Management buttons */}
          {(onManageScreensaver || onReloadDevices) && (
            <div className="mt-3 md:mt-4 pt-3 md:pt-4 border-t border-border space-y-2">
              {onManageScreensaver && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onManageScreensaver(municipality)}
                  className="w-full text-xs md:text-sm hover:bg-secondary hover:text-secondary-foreground hover:border-secondary transition-colors"
                >
                  <ImageIcon className="h-3.5 w-3.5 md:h-4 md:w-4 mr-1.5 md:mr-2" />
                  Gestionar Salvapantallas
                </Button>
              )}
              {onReloadDevices && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onReloadDevices(municipality)}
                  className="w-full text-xs md:text-sm hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors"
                >
                  <RefreshCw className="h-3.5 w-3.5 md:h-4 md:w-4 mr-1.5 md:mr-2" />
                  Recargar dispositivos
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
